import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { SplitText } from 'gsap/SplitText';
import PropTypes from 'prop-types';
import React from 'react';

import * as Icons from '$partials/icons/index.js';

gsap.registerPlugin(SplitText, useGSAP);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const AnimatedStatedHighlightItem = (props) => {
    return (
        <span className={`animated-statement__highlight animated-statement__highlight--${props.color || 'black'}`}>{props.text} </span>
    );
};

const AnimatedStatedIconItem = (props) => {
    const Icon = Icons?.['Icon' + props.icon] || '';

    if (Icon) {
        return (
            <span className={`animated-statement__icon animated-statement__icon--${props.color || 'black'}`}>
                <span><Icon /></span>
            </span>
        );
    } else {
        return null;
    }
};

const AnimatedStatedLinkItem = (props) => {
    const Icon = Icons?.['IconChevronSpriteLarge'] || '';

    return (
        <a
            className='inline-block'
            href={props.url}>
            <span className={`animated-statement__highlight animated-statement__highlight--${props.color || 'orange'}`}>{props.text} </span>

            {Icon && (
                <span className={`animated-statement__icon animated-statement__icon--animated animated-statement__icon--${props.color || 'orange'}`}>
                    <span className='button__icon button__icon--animate'>
                        <Icon />
                    </span>
                </span>
            )}
        </a>
    );
};

const AnimatedStatedScrollItem = (props) => {
    return (
        <span className={`animated-statement__scroll animated-statement__scroll--${props.color || 'black'}`}>
            <span className='animation__scroll-up'>
                {props.items.map((itm, idx) => {
                    return ( <span key={idx}>{itm}</span> );
                }).filter(Boolean)}
            </span>
        </span>
    );
};

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
export const AnimatedStatementText = (props) => {
    // methods
    const convertVariableToComponent = (value) => {
        switch (value?.type) {
            case 'highlight':
                if (value?.content) {
                    return (
                        <AnimatedStatedHighlightItem
                            color={value.color}
                            text={value.content}
                            timeline={props.timeline} />
                    );
                } else {
                    return '';
                }

            case 'icon':
                if (value.icon) {
                    return (
                        <AnimatedStatedIconItem
                            color={value.color}
                            icon={value.icon}
                            timeline={props.timeline} />
                    );
                } else {
                    return '';
                }

            case 'link':
                if (value?.content && value?.url) {
                    return (
                        <AnimatedStatedLinkItem
                            color={value.color}
                            icon={value.icon}
                            text={value.content}
                            timeline={props.timeline}
                            url={value.url} />
                    );
                } else {
                    return '';
                }

            case 'scroll':
                if (Array.isArray(value?.content)) {
                    return (
                        <AnimatedStatedScrollItem
                            color={value.color}
                            items={value.content}
                            timeline={props.timeline} />
                    );
                } else {
                    return '';
                }

            default:
                return '';
        }
    };

    const convertTextToVariable = (stringVariable) => {
        if (stringVariable) {
            const variableData = {};
            stringVariable
                ?.replaceAll('http://', '//')
                ?.replaceAll('https://', '//')
                ?.replaceAll('{{', '')
                ?.replaceAll('}}', '')
                ?.split(',')
                ?.forEach((itm) => {
                    const key = itm?.split(':')?.[0];
                    let val = itm?.split(':')?.[1];

                    if (key === 'content') {
                        val = val.replaceAll('_', ' ');
                        if (val.includes('|')) {
                            val = val.split('|');
                        }
                    }

                    return variableData[key] = val;
                });

            return convertVariableToComponent(variableData);
        }
    };

    const convertText = (text) => {
        if (text) {
            return text.split(' ').map((itm, idx) => {
                const matches = itm.match(/{{.+?}}/g);

                if (matches) {
                    return matches.map((match, jdx) => (
                        <React.Fragment key={jdx}>
                            {convertTextToVariable(match)}
                        </React.Fragment>
                    ));
                } else {
                    return (<span key={idx}>{itm} </span>);
                }
            });
        } else {
            return text;
        }
    };

    // render
    return (
        <div className='animated-statement__lines animation__mask-slide-in'>
            {convertText(props.children)}
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    children: PropTypes.any,
    timeline: PropTypes.object
};

AnimatedStatementText.propTypes = propTypes;

export default AnimatedStatementText;
